import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Calendar } from '../components/ui/calendar';
import { toast } from 'sonner';
import axios from 'axios';
import { CalendarDays, Clock, CreditCard, CheckCircle, ArrowLeft, ArrowRight, User, Mail, Phone } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Booking = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const quoteId = searchParams.get('quoteId');

  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [step, setStep] = useState(1); // 1: date, 2: time, 3: confirm
  const [selectedDate, setSelectedDate] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!quoteId) {
      setError('No quote ID provided. Please use the booking link sent to you.');
      setLoading(false);
      return;
    }
    fetchQuote();
  }, [quoteId]);

  const fetchQuote = async () => {
    try {
      const response = await axios.get(`${API}/quotes/${quoteId}`);
      const q = response.data;
      if (q.status !== 'approved' && q.status !== 'paid') {
        setError('This quote has not been approved yet. Please wait for approval before booking.');
        setLoading(false);
        return;
      }
      setQuote(q);
    } catch (err) {
      setError('Quote not found. Please check your booking link.');
    } finally {
      setLoading(false);
    }
  };

  const fetchSlots = async (date) => {
    setLoadingSlots(true);
    try {
      const dateStr = date.toISOString().split('T')[0];
      const response = await axios.get(`${API}/bookings/available-slots?date=${dateStr}`);
      setAvailableSlots(response.data.slots);
      if (response.data.closed) {
        toast.info('We are closed on this day');
      }
    } catch (err) {
      toast.error('Failed to load available times');
    } finally {
      setLoadingSlots(false);
    }
  };

  const handleDateSelect = (date) => {
    setSelectedDate(date);
    setSelectedSlot(null);
    fetchSlots(date);
  };

  const handleConfirmBooking = async () => {
    if (!selectedDate || !selectedSlot) return;
    setSubmitting(true);
    try {
      const dateStr = selectedDate.toISOString().split('T')[0];
      // Create booking
      const bookingRes = await axios.post(`${API}/bookings`, {
        quoteId: quoteId,
        date: dateStr,
        timeSlot: selectedSlot,
        bookingType: 'cleaning'
      });

      const bookingId = bookingRes.data.id;

      // Create deposit payment
      const payRes = await axios.post(`${API}/bookings/${bookingId}/pay-deposit`, {
        originUrl: window.location.origin
      });

      // Redirect to Stripe
      if (payRes.data.url) {
        window.location.href = payRes.data.url;
      }
    } catch (err) {
      const detail = err.response?.data?.detail || 'Failed to create booking';
      toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  const disabledDays = useMemo(() => {
    return [
      { dayOfWeek: [0] }, // Sundays
      { before: new Date() } // Past dates
    ];
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-sky-50 to-emerald-50">
        <Card className="p-8 text-center">
          <CalendarDays className="h-8 w-8 animate-pulse text-sky-600 mx-auto" />
          <p className="text-slate-600 mt-4">Loading booking details...</p>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-sky-50 to-emerald-50 px-4">
        <Card className="p-8 text-center max-w-md">
          <div className="text-red-500 text-5xl mb-4">!</div>
          <h2 className="text-xl font-bold text-slate-900 mb-2">Booking Unavailable</h2>
          <p className="text-slate-600 mb-6">{error}</p>
          <Button onClick={() => navigate('/contact')} className="bg-sky-600 hover:bg-sky-700">
            Request a Quote
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-50 to-emerald-50 py-8" data-testid="booking-page">
      <div className="container mx-auto px-4 max-w-4xl">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-2">Book Your Appointment</h1>
          <p className="text-slate-600">Select a date and time for your cleaning service</p>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center justify-center gap-2 mb-8">
          {[
            { num: 1, label: 'Date' },
            { num: 2, label: 'Time' },
            { num: 3, label: 'Confirm' }
          ].map((s, i) => (
            <React.Fragment key={s.num}>
              {i > 0 && <div className={`h-0.5 w-12 ${step >= s.num ? 'bg-sky-600' : 'bg-slate-200'}`} />}
              <div className="flex flex-col items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold
                  ${step >= s.num ? 'bg-sky-600 text-white' : 'bg-slate-200 text-slate-500'}`}>
                  {step > s.num ? <CheckCircle className="h-4 w-4" /> : s.num}
                </div>
                <span className="text-xs text-slate-500 mt-1">{s.label}</span>
              </div>
            </React.Fragment>
          ))}
        </div>

        {/* Quote Summary */}
        <Card className="mb-6 border-sky-100">
          <CardContent className="pt-6">
            <div className="flex flex-wrap gap-4 items-center justify-between">
              <div className="flex items-center gap-3">
                <User className="h-5 w-5 text-sky-600" />
                <div>
                  <p className="font-semibold text-slate-900">{quote.name}</p>
                  <p className="text-sm text-slate-500 capitalize">{quote.serviceType.replace('-', ' ')}</p>
                </div>
              </div>
              <div className="flex gap-3 items-center">
                <Badge className="bg-emerald-600 text-white">Quote: £{quote.quoteAmount?.toFixed(2)}</Badge>
                <Badge variant="outline" className="border-sky-600 text-sky-600">Deposit: £15.00</Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Step 1: Date Selection */}
        {step === 1 && (
          <Card data-testid="step-date">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <CalendarDays className="h-5 w-5 text-sky-600" />
                Select a Date
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col items-center">
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={handleDateSelect}
                  disabled={disabledDays}
                  className="rounded-lg border shadow-sm"
                />
                <div className="mt-4 text-sm text-slate-500 space-y-1 text-center">
                  <p>Mon-Fri: 8:00 AM - 7:00 PM</p>
                  <p>Saturday: 10:00 AM - 1:00 PM</p>
                  <p>Sunday: Closed</p>
                </div>
                {selectedDate && (
                  <Button
                    onClick={() => setStep(2)}
                    className="mt-6 bg-sky-600 hover:bg-sky-700"
                    data-testid="next-to-time"
                  >
                    Next: Choose Time <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 2: Time Slot Selection */}
        {step === 2 && (
          <Card data-testid="step-time">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Clock className="h-5 w-5 text-sky-600" />
                Select a Time Slot — {selectedDate?.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loadingSlots ? (
                <p className="text-center text-slate-500 py-8">Loading available times...</p>
              ) : availableSlots.length === 0 ? (
                <p className="text-center text-slate-500 py-8">No time slots available for this date.</p>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {availableSlots.map((slot) => (
                    <button
                      key={slot.value}
                      disabled={!slot.available}
                      onClick={() => setSelectedSlot(slot.value)}
                      data-testid={`slot-${slot.value}`}
                      className={`p-4 rounded-lg border-2 text-left transition-all
                        ${!slot.available 
                          ? 'border-slate-100 bg-slate-50 text-slate-300 cursor-not-allowed' 
                          : selectedSlot === slot.value
                            ? 'border-sky-600 bg-sky-50 text-sky-700 shadow-md'
                            : 'border-slate-200 hover:border-sky-300 hover:bg-sky-50/50 cursor-pointer'
                        }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-semibold">{slot.label}</p>
                          <p className="text-sm">{slot.value.replace('-', ' - ')}</p>
                        </div>
                        {!slot.available && <Badge variant="secondary" className="text-xs">Booked</Badge>}
                        {selectedSlot === slot.value && <CheckCircle className="h-5 w-5 text-sky-600" />}
                      </div>
                    </button>
                  ))}
                </div>
              )}
              <div className="flex gap-3 mt-6">
                <Button onClick={() => setStep(1)} variant="outline" data-testid="back-to-date">
                  <ArrowLeft className="h-4 w-4 mr-2" /> Back
                </Button>
                {selectedSlot && (
                  <Button onClick={() => setStep(3)} className="bg-sky-600 hover:bg-sky-700" data-testid="next-to-confirm">
                    Next: Confirm <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 3: Confirmation */}
        {step === 3 && (
          <Card data-testid="step-confirm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <CheckCircle className="h-5 w-5 text-sky-600" />
                Confirm Your Booking
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="bg-slate-50 rounded-lg p-6 space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-slate-500">Customer</p>
                    <p className="font-semibold text-slate-900">{quote.name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Service</p>
                    <p className="font-semibold text-slate-900 capitalize">{quote.serviceType.replace('-', ' ')}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Date</p>
                    <p className="font-semibold text-slate-900">
                      {selectedDate?.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Time</p>
                    <p className="font-semibold text-slate-900">{selectedSlot?.replace('-', ' - ')}</p>
                  </div>
                </div>

                <hr className="border-slate-200" />

                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Total Service Cost</span>
                    <span className="font-semibold">£{quote.quoteAmount?.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Booking Deposit (pay now)</span>
                    <span className="font-semibold text-sky-600">£15.00</span>
                  </div>
                  <div className="flex justify-between text-sm border-t pt-2">
                    <span className="text-slate-600">Remaining (pay on completion)</span>
                    <span className="font-semibold">£{(quote.quoteAmount - 15).toFixed(2)}</span>
                  </div>
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <Button onClick={() => setStep(2)} variant="outline" data-testid="back-to-time">
                  <ArrowLeft className="h-4 w-4 mr-2" /> Back
                </Button>
                <Button
                  onClick={handleConfirmBooking}
                  disabled={submitting}
                  className="bg-emerald-600 hover:bg-emerald-700 flex-1"
                  data-testid="confirm-booking-btn"
                >
                  <CreditCard className="h-4 w-4 mr-2" />
                  {submitting ? 'Processing...' : 'Pay £15.00 Deposit & Confirm'}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default Booking;
